import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { DateService } from "@root/shared/service/date.service";
import * as moment from "moment";
import _ from "lodash";
import { PlatformType } from "@root/config";
import { RulesSettingsService } from "./rules-settings.service";
import { HttpClient } from "@angular/common/http";
import { FormBuilder, FormGroup } from "@angular/forms";
import { NzMessageService } from "ng-zorro-antd/message";
import { TranslateService } from "@root/shared/service/translate.service";
import { BatchModifyRestrictionsService } from "@root/router/price/batch-modify-restrictions/batch-modify-restrictions.service";

interface RelevantUnit {
  type: "house" | "type";
  id: number;
  title: string;
  checked?: boolean;
}

@Component({
  selector: "os-rules-setting",
  templateUrl: "./rules-settings.component.html",
  styleUrls: ["./rules-settings.component.scss"],
})
export class OSRulesSettingComponent implements OnInit {
  private readonly GetRulesApi = "/api/calendar/get_rule";
  private readonly SetRulesApi = "/api/calendar/set_rule";
  readonly Platform: typeof PlatformType = PlatformType;
  @Input() dateRange: Array<string | Date>;
  @Input() relevantUnits: RelevantUnit[] = [];
  @Output() success = new EventEmitter();
  loading = false;
  form: FormGroup = this.fb.group({
    min_stay_through: null,
    max_stay_through: null,
    closed_on_arrival: false,
    closed_on_departure: false,
    min_stay_on_arrival: null,
    max_stay_on_arrival: null,
  });

  //#region Control Configs

  readonly mixedPlaceholder = this.t.t("calendar-hostex_sidebar-rules_html_ph_mixed");
  readonly daysPlaceholder = this.t.t("calendar-hostex_sidebar-rules_html_ph_days");
  readonly controlOpts = [
    {
      name: "min_stay_on_arrival",
      type: "input",
      label: this.t.t("operate_sidebar-rules_settings_html_min_stay_on_arrival"),
      tooltip: this.t.t("operate_sidebar-rules_settings_html_min_stay_on_arrival_tip"),
      platforms: this.bmrSrv.platformsSupportThisRule("min_stay_on_arrival"),
    },
    {
      name: "closed_on_arrival",
      type: "checkbox",
      label: this.t.t("operate_sidebar-rules_settings_html_closed_on_arrival"),
      tooltip: this.t.t("operate_sidebar-rules_settings_html_closed_on_arrival_tip"),
      platforms: this.bmrSrv.platformsSupportThisRule("closed_on_arrival"),
    },
    {
      name: "closed_on_departure",
      type: "checkbox",
      label: this.t.t("operate_sidebar-rules_settings_html_closed_on_departure"),
      tooltip: this.t.t("operate_sidebar-rules_settings_html_closed_on_departure_tip"),
      platforms: this.bmrSrv.platformsSupportThisRule("closed_on_departure"),
    },
    {
      name: "max_stay_on_arrival",
      type: "input",
      label: this.t.t("operate_sidebar-rules_settings_html_max_stay_on_arrival"),
      tooltip: this.t.t("operate_sidebar-rules_settings_html_max_stay_on_arrival_tip"),
      platforms: this.bmrSrv.platformsSupportThisRule("max_stay_on_arrival"),
    },
    {
      name: "min_stay_through",
      type: "input",
      label: this.t.t("operate_sidebar-rules_settings_html_min_stay_through"),
      tooltip: this.t.t("operate_sidebar-rules_settings_html_min_stay_through_tip"),
      platforms: this.bmrSrv.platformsSupportThisRule("min_stay_through"),
    },
    {
      name: "max_stay_through",
      type: "input",
      label: this.t.t("operate_sidebar-rules_settings_html_max_stay_through"),
      tooltip: this.t.t("operate_sidebar-rules_settings_html_max_stay_through_tip"),
      platforms: this.bmrSrv.platformsSupportThisRule("max_stay_through"),
    },
  ];

  //#endregion

  nzFormat = this.dateService.getAngularFormat(["YMD"]);
  disabledDate = (date: string | Date) => {
    const head = moment().subtract(1, "d");
    const last = moment().add(2, "year");

    return !moment(date).isBetween(head, last, "d");
  };
  get nights() {
    const head = moment(_.head(this.dateRange));
    const last = moment(_.last(this.dateRange));
    return last.diff(head, "days") + 1;
  }

  constructor(
    private dateService: DateService,
    private rsSrv: RulesSettingsService,
    private http: HttpClient,
    private msg: NzMessageService,
    private t: TranslateService,
    private bmrSrv: BatchModifyRestrictionsService,
    private fb: FormBuilder,
  ) {}

  ngOnInit() {
    /**
     * 默认选中所有的单元
     */
    this.relevantUnits = this.relevantUnits.map((unit) => ({
      ...unit,
      checked: true,
    }));

    this.fetchRulesSettings();
  }

  private fetchRulesSettings() {
    const params = {
      start_date: moment(this.dateRange?.[0]).format("YYYY-MM-DD"),
      end_date: moment(this.dateRange?.[1]).format("YYYY-MM-DD"),
    };

    const [houseId, rtId] = this.getHouseIdAndRtId();
    if (houseId) {
      Object.assign(params, { house_id: houseId });
    }
    if (rtId) {
      Object.assign(params, { house_type_id: rtId });
    }

    this.http.get(this.GetRulesApi, { params }).subscribe((rst: any) => {
      const data = {
        ...rst.data,
        // closed_on_arrival: 'Mixed',
      };

      this.form.patchValue(data);
    });
  }

  private requestUpdateSettings() {
    const value = this.form.value;
    const params = {
      start_date: moment(this.dateRange?.[0]).format("YYYY-MM-DD"),
      end_date: moment(this.dateRange?.[1]).format("YYYY-MM-DD"),
      ...value,
    };

    const [houseId, rtId] = this.getHouseIdAndRtId();
    if (houseId) {
      Object.assign(params, { house_id: houseId });
    }
    if (rtId) {
      Object.assign(params, { house_type_id: rtId });
    }

    this.http.post(this.SetRulesApi, params).subscribe((rst: any) => {
      this.loading = false;
      if (rst.error_code === 0) {
        this.msg.success(this.t.t("word.success"));
      }
    });
  }

  private getHouseIdAndRtId(): [number, number] {
    const houseId = this.relevantUnits.find((item) => item.type === "house" && item.checked)?.id;
    const rtId = this.relevantUnits.find((item) => item.type === "type" && item.checked)?.id;
    return [houseId, rtId];
  }

  onClickCancel() {
    this.success.emit();
  }

  onClickSave() {
    this.loading = true;
    this.requestUpdateSettings();
    // this.rsSrv.save();
  }
}
